import {
  BarChart3,
  Building2,
  GitCompareArrows,
  Globe,
  KeyRound,
  Link2,
  type LucideIcon,
  Shield,
  Sparkles,
  Tag,
  User,
  Users,
} from "lucide-react";
import { matchPath } from "react-router";

import { routes } from "~/routes";

import { administersOwnTenant, type Session } from "./session";

/** What a caller must satisfy to see an admin entry; every guard listed must pass. */
export type AdminGuard = "platformAdmin" | "tenantAdmin" | "identityV2Only" | "infosec";

/** Where an entry lives: the platform admin console under /console, or the tenant's Admin Panel under /admin. */
export type AdminSurface = "console" | "tenant";

export type AdminEntry = {
  id: string;
  /** Navigation label. */
  label: string;
  /** Document title the route sets. */
  title: string;
  path: string;
  icon: LucideIcon;
  guards: AdminGuard[];
  surface: AdminSurface;
  /** Breadcrumb label when it differs from the navigation label. */
  breadcrumb?: string;
  /** false for an entry that contributes a route, title and breadcrumb but no navigation item. */
  nav?: false;
  /** The id of the entry this one nests under; its crumb precedes this one's and its nav item stays highlighted. */
  parent?: string;
};

export function guardPasses(guard: AdminGuard, session: Session, infosecEnabled: boolean): boolean {
  switch (guard) {
    case "platformAdmin":
      return session.isPlatformAdmin;
    case "tenantAdmin":
      return administersOwnTenant(session);
    case "identityV2Only":
      return session.mode === "identity-v2";
    case "infosec":
      return infosecEnabled;
  }
}

export function entryAllowed(entry: AdminEntry, session: Session, infosecEnabled: boolean): boolean {
  return entry.guards.every((guard) => guardPasses(guard, session, infosecEnabled));
}

/** Every entry of one surface, navigable or not, in registry order. */
export function surfaceEntries(surface: AdminSurface, entries: readonly AdminEntry[] = adminEntries): AdminEntry[] {
  return entries.filter((entry) => entry.surface === surface);
}

/** The entries of one surface that contribute a navigation item, in order. */
export function navigableEntries(surface: AdminSurface, entries: readonly AdminEntry[] = adminEntries): AdminEntry[] {
  return surfaceEntries(surface, entries).filter((entry) => entry.nav !== false);
}

/** The entry whose path pattern matches the current location, parameterised entries included. */
export function adminEntryForPath(
  pathname: string,
  entries: readonly AdminEntry[] = adminEntries,
): AdminEntry | undefined {
  return entries.find((entry) => matchPath(entry.path, pathname));
}

/** The entry `entry.parent` names, if any. */
export function adminEntryParent(
  entry: AdminEntry,
  entries: readonly AdminEntry[] = adminEntries,
): AdminEntry | undefined {
  return entry.parent === undefined ? undefined : entries.find((candidate) => candidate.id === entry.parent);
}

/** The entries `entry` nests under, outermost first. */
export function adminEntryAncestors(entry: AdminEntry, entries: readonly AdminEntry[] = adminEntries): AdminEntry[] {
  const parent = adminEntryParent(entry, entries);
  return parent ? [...adminEntryAncestors(parent, entries), parent] : [];
}

/** Whether `entry`'s navigation item is highlighted at `pathname`: the location is its route or a descendant's. */
export function navEntryActive(
  entry: AdminEntry,
  pathname: string,
  entries: readonly AdminEntry[] = adminEntries,
): boolean {
  const current = adminEntryForPath(pathname, entries);
  return (
    current !== undefined &&
    (current.id === entry.id || adminEntryAncestors(current, entries).some((ancestor) => ancestor.id === entry.id))
  );
}

/** The first page of a surface this caller may open, used to redirect an administrator refused elsewhere. */
export function firstAllowedAdminPath(
  session: Session,
  infosecEnabled: boolean,
  surface: AdminSurface = "tenant",
  entries: readonly AdminEntry[] = adminEntries,
): string | undefined {
  return navigableEntries(surface, entries).find((entry) => entryAllowed(entry, session, infosecEnabled))?.path;
}

const consoleGuards: AdminGuard[] = ["platformAdmin", "identityV2Only"];

// The admin area's pages as data, in navigation order per surface; a new screen adds one entry here.
export const adminEntries: AdminEntry[] = [
  {
    id: "users",
    label: "Users",
    title: "Users Management",
    path: routes.AdminUsers,
    icon: Users,
    guards: [],
    surface: "tenant",
  },
  {
    id: "user",
    label: "User",
    title: "User",
    path: routes.AdminUser,
    icon: User,
    guards: ["identityV2Only", "tenantAdmin"],
    surface: "tenant",
    nav: false,
    parent: "users",
  },
  {
    id: "control-tags",
    label: "Control Tags",
    title: "Control Tags",
    path: routes.AdminControlTags,
    icon: Tag,
    guards: [],
    surface: "tenant",
  },
  {
    id: "infosec",
    label: "Infosec Levels",
    title: "Infosec Level Management",
    path: routes.AdminInfosec,
    icon: Shield,
    guards: ["tenantAdmin", "infosec"],
    surface: "tenant",
  },
  {
    id: "integrations",
    label: "App Integrations",
    title: "Integrations",
    path: routes.AdminIntegrations,
    icon: Link2,
    guards: [],
    surface: "tenant",
    breadcrumb: "Integrations",
  },
  {
    id: "encryption-keys",
    label: "Encryption Keys",
    title: "Encryption Keys",
    path: routes.AdminEncryptionKeys,
    icon: KeyRound,
    guards: ["tenantAdmin"],
    surface: "tenant",
  },
  {
    id: "secure-connections",
    label: "Secure Connections",
    title: "Secure Connections",
    path: routes.AdminSecureConnections,
    icon: Globe,
    guards: ["tenantAdmin"],
    surface: "tenant",
  },
  {
    id: "usage",
    label: "Usage Metrics",
    title: "Usage Metrics",
    path: routes.AdminUsage,
    icon: BarChart3,
    guards: [],
    surface: "tenant",
  },
  {
    id: "compatibility",
    label: "Compatibility",
    title: "Compatibility",
    path: routes.AdminCompatibility,
    icon: GitCompareArrows,
    guards: ["tenantAdmin"],
    surface: "tenant",
  },
  {
    id: "ai",
    label: "LLMs and AI Instructions",
    title: "LLMs and AI Instructions",
    path: routes.AdminAi,
    icon: Sparkles,
    guards: ["tenantAdmin"],
    surface: "tenant",
  },
  {
    id: "tenants",
    label: "Tenants",
    title: "Tenants",
    path: routes.ConsoleTenants,
    icon: Building2,
    guards: consoleGuards,
    surface: "console",
  },
  {
    id: "tenant",
    label: "Tenant",
    title: "Tenant",
    path: routes.ConsoleTenant,
    icon: Building2,
    guards: consoleGuards,
    surface: "console",
    nav: false,
    parent: "tenants",
  },
  {
    id: "console-users",
    label: "Users",
    title: "Users",
    path: routes.ConsoleUsers,
    icon: Users,
    guards: consoleGuards,
    surface: "console",
  },
  {
    id: "console-user",
    label: "User",
    title: "User",
    path: routes.ConsoleUser,
    icon: User,
    guards: consoleGuards,
    surface: "console",
    nav: false,
    parent: "console-users",
  },
];
